import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "button", "overwrite", "status"]
  static values = { url: String, messages: Object }

  disconnect() {
    if (this.abortController) this.abortController.abort()
  }

  readField(field) {
    if (field.dataset.translationField === "content") {
      return $(field).summernote("isEmpty") ? "" : $(field).summernote("code")
    }
    return field.value
  }

  async translate(event) {
    event.preventDefault()
    if (this.busy) return

    const source = event.currentTarget.closest("[data-language]")
    const sourceLanguage = source.dataset.language
    const fields = Array.from(source.querySelectorAll("[data-translation-field]"))
      .map(field => ({ name: field.dataset.translationField, text: this.readField(field) }))
      .filter(field => field.text.trim())

    if (!fields.length) {
      this.showStatus("empty", true)
      return
    }

    const jobs = []
    this.panelTargets.filter(panel => panel !== source).forEach(panel => {
      fields.forEach(({ name, text }) => {
        const field = panel.querySelector(`[data-translation-field="${name}"]`)
        const previous = this.readField(field)
        if (previous.trim() && !this.overwriteTarget.checked) return
        jobs.push({ field, previous, text, language: panel.dataset.language, name })
      })
    })

    if (!jobs.length) {
      this.showStatus("nothing")
      return
    }

    this.busy = true
    this.buttonTargets.forEach(button => { button.disabled = true })
    const submitButtons = Array.from(this.element.closest("form").querySelectorAll('[type="submit"]'))
      .filter(button => !button.disabled)
    submitButtons.forEach(button => { button.disabled = true })
    this.showStatus("loading")
    this.abortController = new AbortController()
    const timeout = setTimeout(() => this.abortController.abort(), 30000)

    try {
      const results = await Promise.all(jobs.map(async job => {
        const response = await fetch(this.urlValue, {
          method: "POST",
          credentials: "same-origin",
          signal: this.abortController.signal,
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
          },
          body: JSON.stringify({
            source_language: sourceLanguage.toUpperCase(),
            target_language: job.language.toUpperCase(),
            source_text: job.text,
            tag_handling: job.name === "content" ? "html" : "plain"
          })
        })
        if (!response.ok) throw new Error("Translation failed")
        const data = await response.json()
        if (typeof data.translated_text !== "string" || !data.translated_text.trim()) {
          throw new Error("Invalid translation response")
        }
        return { ...job, translated: data.translated_text }
      }))

      let skipped = false
      results.forEach(({ field, previous, translated, name }) => {
        // Preserve edits made while waiting for DeepL.
        if (this.readField(field) !== previous) {
          skipped = true
          return
        }
        field.value = translated
        if (name === "content") $(field).summernote("code", translated)
        field.dispatchEvent(new Event("change", { bubbles: true }))
      })
      this.showStatus(skipped ? "changed" : "success")
    } catch (error) {
      this.abortController.abort()
      this.showStatus("error", true)
    } finally {
      clearTimeout(timeout)
      this.busy = false
      this.buttonTargets.forEach(button => { button.disabled = false })
      submitButtons.forEach(button => { button.disabled = false })
    }
  }

  showStatus(message, error = false) {
    this.statusTarget.textContent = this.messagesValue[message]
    this.statusTarget.classList.toggle("text-danger", error)
  }
}
